/* eslint-disable complexity */
import { browserName, deviceDetect } from 'react-device-detect';
import DeviceInfo from 'react-native-device-info';

import { ELoginMethod } from '@webtrader/core/api/user/types';
import appsFlyerSDK, { IDeepLinkData } from '@webtrader/core/appsflyer';
import { DEMO_LOGINS, ELoginAs } from '@webtrader/core/constants/account';
import { useForm } from '@webtrader/core/form';
import { getQueryLangFromTLang } from '@webtrader/core/format/i18n';
import { ILoginCredentialsFormValue, getLoginCredentials } from '@webtrader/core/format/login';
import { useMarketingPageUrl } from '@webtrader/core/hooks/use-marketing-page-url';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { authService } from '@webtrader/core/services/auth';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { getMarketingPageUrl } from '@webtrader/core/utils/get-marketing-page-url';
import { checkIsNative } from '@webtrader/core/validators/device';

import { AlertManager } from '@/components/Alert/Manager';
import { syncBiometricCredentials } from '@/utils/biometrics';

import { useBiometrics } from './useBiometrics';

interface IUseLoginFormHookProps {
  selectedTab: ELoginAs;
  loginMethod?: ELoginMethod;
}

const IS_MOBILE_WEB = !checkIsNative();

export const useLoginFormHook = (props: IUseLoginFormHookProps) => {
  const { selectedTab, loginMethod } = props;

  const deviceDetectInfo = IS_MOBILE_WEB ? deviceDetect(window.navigator.userAgent) : undefined;
  const isDemo = DEMO_LOGINS.includes(selectedTab);

  const { isBiometricEnable } = useBiometrics();

  useMarketingPageUrl();

  const { values, submitForm, setFieldValue } = useForm<ILoginCredentialsFormValue>({
    initialValues: { userId: '', password: '' },
    onSubmit: async (formValues) => {
      const { userId, password } = getLoginCredentials({ loginMethod, ...formValues });

      if (userId && password) {
        const attribution =
          appsFlyerSDK.getStoredDeepLinkData() ?? storage.getItem<IDeepLinkData>(EStorageKey.ATTRIBUTION_DATA);
        const marketingPageUrl = isDemo ? getMarketingPageUrl(attribution?.link) : undefined;
        const language = isDemo ? getQueryLangFromTLang() : undefined;
        const { success, message } = await authService.login({
          userId,
          password,
          deviceId: IS_MOBILE_WEB ? undefined : await DeviceInfo.getUniqueId(),
          browserType: IS_MOBILE_WEB ? browserName ?? undefined : undefined,
          deviceType: IS_MOBILE_WEB ? deviceDetectInfo?.os ?? undefined : undefined,
          marketingPageUrl,
          language,
          onBeforeSuccess: async () => {
            if (isBiometricEnable && !IS_MOBILE_WEB && !isDemo) {
              await syncBiometricCredentials(userId, password);
            }
          },
          /**
           * WT-9768: Disable change language function only for login via mobile app (where issue is seen)
           * as user language should already be set
           */
          disableChangeLang: !IS_MOBILE_WEB
        });

        if (!success && message !== mapResponseCodeToMessage(ECommonResponseCode.AX_1005)) {
          AlertManager().alert({
            message
          });
        }
      }
    }
  });

  return {
    values,
    submitForm,
    setFieldValue
  };
};
